import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  EmailTemplate,
  EmailLog,
  IncomingEmail,
  RenderedEmail,
  CreateTemplateDto,
  UpdateTemplateDto,
  SendEmailDto,
  SendTemplateEmailDto,
  SendBewerberEmailDto,
  QueryEmailLogDto,
  QueryIncomingEmailDto,
  LinkEmailDto,
  ApiResponse,
} from '../types/email';

// Query keys
export const emailKeys = {
  all: ['email'] as const,
  templates: () => [...emailKeys.all, 'templates'] as const,
  templatesList: () => [...emailKeys.templates(), 'list'] as const,
  templatesActive: () => [...emailKeys.templates(), 'active'] as const,
  templateDetail: (id: string) => [...emailKeys.templates(), 'detail', id] as const,
  templateByName: (name: string) => [...emailKeys.templates(), 'name', name] as const,
  logs: () => [...emailKeys.all, 'logs'] as const,
  logsList: (filters: QueryEmailLogDto) => [...emailKeys.logs(), 'list', filters] as const,
  logDetail: (id: string) => [...emailKeys.logs(), 'detail', id] as const,
  incoming: () => [...emailKeys.all, 'incoming'] as const,
  incomingList: (filters: QueryIncomingEmailDto) => [...emailKeys.incoming(), 'list', filters] as const,
  incomingDetail: (id: string) => [...emailKeys.incoming(), 'detail', id] as const,
  bewerberEmails: (bewerberId: string) => [...emailKeys.all, 'bewerber', bewerberId] as const,
};

// ==================
// TEMPLATE API FUNCTIONS
// ==================

const fetchTemplates = async (activeOnly = false): Promise<ApiResponse<EmailTemplate[]>> => {
  const params = new URLSearchParams();
  if (activeOnly) params.append('activeOnly', 'true');
  const response = await apiClient.get(`/email/templates?${params.toString()}`);
  return response.data;
};

const fetchTemplate = async (id: string): Promise<ApiResponse<EmailTemplate>> => {
  const response = await apiClient.get(`/email/templates/${id}`);
  return response.data;
};

const createTemplate = async (data: CreateTemplateDto): Promise<ApiResponse<EmailTemplate>> => {
  const response = await apiClient.post('/email/templates', data);
  return response.data;
};

const updateTemplate = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateTemplateDto;
}): Promise<ApiResponse<EmailTemplate>> => {
  const response = await apiClient.patch(`/email/templates/${id}`, data);
  return response.data;
};

const deleteTemplate = async (id: string): Promise<void> => {
  await apiClient.delete(`/email/templates/${id}`);
};

const previewTemplate = async ({
  id,
  data,
}: {
  id: string;
  data: Record<string, unknown>;
}): Promise<ApiResponse<RenderedEmail>> => {
  const response = await apiClient.post(`/email/templates/${id}/preview`, { data });
  return response.data;
};

// ==================
// EMAIL SENDING API FUNCTIONS
// ==================

const sendEmail = async (data: SendEmailDto): Promise<ApiResponse<EmailLog>> => {
  const response = await apiClient.post('/email/send', data);
  return response.data;
};

const sendTemplateEmail = async (data: SendTemplateEmailDto): Promise<ApiResponse<EmailLog>> => {
  const response = await apiClient.post('/email/send-template', data);
  return response.data;
};

const sendBewerberEmail = async ({
  bewerberId,
  data,
}: {
  bewerberId: string;
  data: SendBewerberEmailDto;
}): Promise<ApiResponse<EmailLog>> => {
  const response = await apiClient.post(`/bewerber/${bewerberId}/email`, data);
  return response.data;
};

const previewBewerberEmail = async ({
  bewerberId,
  data,
}: {
  bewerberId: string;
  data: { templateName: string; data: Record<string, unknown> };
}): Promise<ApiResponse<RenderedEmail>> => {
  const response = await apiClient.post(`/bewerber/${bewerberId}/email/preview`, data);
  return response.data;
};

// ==================
// EMAIL LOG API FUNCTIONS
// ==================

const fetchEmailLogs = async (query: QueryEmailLogDto): Promise<ApiResponse<EmailLog[]>> => {
  const params = new URLSearchParams();
  if (query.status) params.append('status', query.status);
  if (query.bewerberId) params.append('bewerberId', query.bewerberId);
  if (query.bedarfId) params.append('bedarfId', query.bedarfId);
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);

  const response = await apiClient.get(`/email/logs?${params.toString()}`);
  return response.data;
};

const fetchEmailLog = async (id: string): Promise<ApiResponse<EmailLog>> => {
  const response = await apiClient.get(`/email/logs/${id}`);
  return response.data;
};

const fetchBewerberEmails = async ({
  bewerberId,
  page,
  limit,
}: {
  bewerberId: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<EmailLog[]>> => {
  const params = new URLSearchParams();
  if (page) params.append('page', page.toString());
  if (limit) params.append('limit', limit.toString());

  const response = await apiClient.get(`/bewerber/${bewerberId}/emails?${params.toString()}`);
  return response.data;
};

// ==================
// INCOMING EMAIL API FUNCTIONS
// ==================

const fetchIncomingEmails = async (query: QueryIncomingEmailDto): Promise<ApiResponse<IncomingEmail[]>> => {
  const params = new URLSearchParams();
  if (query.status) params.append('status', query.status);
  if (query.bewerberId) params.append('bewerberId', query.bewerberId);
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);

  const response = await apiClient.get(`/email/incoming?${params.toString()}`);
  return response.data;
};

const fetchIncomingEmail = async (id: string): Promise<ApiResponse<IncomingEmail>> => {
  const response = await apiClient.get(`/email/incoming/${id}`);
  return response.data;
};

const linkIncomingEmail = async ({
  id,
  data,
}: {
  id: string;
  data: LinkEmailDto;
}): Promise<ApiResponse<IncomingEmail>> => {
  const response = await apiClient.post(`/email/incoming/${id}/link`, data);
  return response.data;
};

const ignoreIncomingEmail = async (id: string): Promise<ApiResponse<IncomingEmail>> => {
  const response = await apiClient.post(`/email/incoming/${id}/ignore`);
  return response.data;
};

const processInbox = async (): Promise<ApiResponse<{ processed: number }>> => {
  const response = await apiClient.post('/email/process-inbox');
  return response.data;
};

// ==================
// TEMPLATE HOOKS
// ==================

export function useEmailTemplates(activeOnly = false) {
  return useQuery({
    queryKey: activeOnly ? emailKeys.templatesActive() : emailKeys.templatesList(),
    queryFn: () => fetchTemplates(activeOnly),
  });
}

export function useEmailTemplate(id: string) {
  return useQuery({
    queryKey: emailKeys.templateDetail(id),
    queryFn: () => fetchTemplate(id),
    enabled: !!id,
  });
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.templates() });
    },
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTemplate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: emailKeys.templates() });
      queryClient.invalidateQueries({ queryKey: emailKeys.templateDetail(data.data.id) });
    },
  });
}

export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.templates() });
    },
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({
    mutationFn: previewTemplate,
  });
}

// ==================
// EMAIL SENDING HOOKS
// ==================

export function useSendEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.logs() });
    },
  });
}

export function useSendTemplateEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendTemplateEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.logs() });
    },
  });
}

export function useSendBewerberEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendBewerberEmail,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: emailKeys.logs() });
      queryClient.invalidateQueries({ queryKey: emailKeys.bewerberEmails(variables.bewerberId) });
    },
  });
}

export function usePreviewBewerberEmail() {
  return useMutation({
    mutationFn: previewBewerberEmail,
  });
}

// ==================
// EMAIL LOG HOOKS
// ==================

export function useEmailLogs(query: QueryEmailLogDto = {}) {
  return useQuery({
    queryKey: emailKeys.logsList(query),
    queryFn: () => fetchEmailLogs(query),
  });
}

export function useEmailLog(id: string) {
  return useQuery({
    queryKey: emailKeys.logDetail(id),
    queryFn: () => fetchEmailLog(id),
    enabled: !!id,
  });
}

export function useBewerberEmails(
  bewerberId: string,
  options?: { page?: number; limit?: number },
) {
  return useQuery({
    queryKey: emailKeys.bewerberEmails(bewerberId),
    queryFn: () => fetchBewerberEmails({ bewerberId, ...options }),
    enabled: !!bewerberId,
  });
}

// ==================
// INCOMING EMAIL HOOKS
// ==================

export function useIncomingEmails(query: QueryIncomingEmailDto = {}) {
  return useQuery({
    queryKey: emailKeys.incomingList(query),
    queryFn: () => fetchIncomingEmails(query),
  });
}

export function useIncomingEmail(id: string) {
  return useQuery({
    queryKey: emailKeys.incomingDetail(id),
    queryFn: () => fetchIncomingEmail(id),
    enabled: !!id,
  });
}

export function useLinkIncomingEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: linkIncomingEmail,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: emailKeys.incoming() });
      queryClient.invalidateQueries({ queryKey: emailKeys.incomingDetail(data.data.id) });
      if (data.data.bewerberId) {
        queryClient.invalidateQueries({ queryKey: emailKeys.bewerberEmails(data.data.bewerberId) });
      }
    },
  });
}

export function useIgnoreIncomingEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ignoreIncomingEmail,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: emailKeys.incoming() });
      queryClient.invalidateQueries({ queryKey: emailKeys.incomingDetail(data.data.id) });
    },
  });
}

export function useProcessInbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processInbox,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.incoming() });
    },
  });
}
